import { Fragment } from 'react';
import { cn } from '@rocco/utils/cn';
import { PRODUCT_LAUNCH_CAPTION } from './product-launch';
import {
    useProductLaunchCountdown,
    type ProductLaunchCountdownState as Countdown,
} from './use-product-launch-countdown';

/**
 * Figma digits are Neue Haas Grotesk Display Pro 25 Thin, but the Typekit kit
 * (hpu6ldu) only carries the display family at 400–700, and browsers don't
 * synthesise lighter weights. Helvetica Neue leads the stack so Apple devices
 * render their genuine Thin face (Neue Haas is a Helvetica revival — near
 * identical); everything else falls back to the brand display family at its
 * lightest available weight. If Display 25 Thin (weight 200) is added to the
 * kit, move `neue-haas-grotesk-display` to the front.
 */
const DIGIT_FONT =
    '"Helvetica Neue", neue-haas-grotesk-display, Helvetica, Arial, sans-serif';

const pad = (value: number): string => String(value).padStart(2, '0');

const groupsFor = (countdown: Countdown) => [
    { unit: 'DAYS', value: countdown.days },
    { unit: 'HOURS', value: countdown.hours },
    { unit: 'MINUTES', value: countdown.minutes },
    { unit: 'SECONDS', value: countdown.seconds },
];

/** Spoken form, so the ticking digits don't have to be read one by one. */
const spokenLabel = (countdown: Countdown): string =>
    `ArchiPro USA public launch. ` +
    `${countdown.days} days, ${countdown.hours} hours, ` +
    `${countdown.minutes} minutes and ${countdown.seconds} seconds to go.`;

/**
 * 120px digits on the 1920 frame; mobile is 40% scale in rem (1rem = 16px at
 * the 414 anchor) so the clock tracks the fluid mobile root font-size.
 */
const DIGIT_CLASS =
    'font-extralight max-md:text-[3rem] max-md:leading-[3.25rem] md:text-[6.25vw] md:leading-[6.771vw] tracking-[-0.03em] tabular-nums';

/** The `:` between groups — raised 20.5px so it sits optically mid-digit. */
const Separator = () => (
    <div className="relative max-md:h-[3.25rem] max-md:w-[0.4rem] md:h-[6.771vw] md:w-[0.833vw]">
        <span
            className={cn(
                'absolute left-1/2 -translate-x-1/2 text-center',
                'max-md:top-[-0.5rem] md:top-[-1.068vw]',
                DIGIT_CLASS,
            )}
            style={{ fontFamily: DIGIT_FONT }}
        >
            :
        </span>
    </div>
);

export interface ProductLaunchCountdownProps {
    className?: string;
    /** Overrides the launch instant. Test seam; production uses the constant. */
    launchMs?: number;
}

/**
 * Counting clock for the US public launch (Figma `4393:20894` hero,
 * `4393:20992` footer — the same 674×183 group in both placements).
 *
 * Caption, then `DD : HH : MM : SS` with unit labels under each pair. Digits
 * are 120px Display Thin on the 1920 frame (sized in vw so the block scales
 * with its section; fixed half-scale px below `md`); caption and unit labels
 * are 17px Text Roman tracked +0.16em. Colour inherits from the surface — the
 * design is white-on-image in the hero and white-on-#1f1f21 in the footer.
 *
 * Renders nothing once the launch instant has passed, so the surfaces that
 * embed it don't have to re-check the date. Visibility per market and feature
 * flag is the caller's job — see `useProductLaunchCountdownEnabled`.
 *
 * Digits use `tabular-nums` so the row doesn't jitter as the seconds change,
 * and are marked `suppressHydrationWarning` because the server-rendered second
 * is a beat behind the one the client computes at hydration.
 */
export const ProductLaunchCountdown = ({
    className,
    launchMs,
}: ProductLaunchCountdownProps) => {
    const countdown = useProductLaunchCountdown(launchMs);

    if (countdown.hasLaunched) {
        return null;
    }

    return (
        <div
            className={cn('flex flex-col items-start', className)}
            role="timer"
            aria-label={spokenLabel(countdown)}
            suppressHydrationWarning
        >
            <p
                className="m-0 font-sans leading-none font-normal tracking-[0.16em] max-md:pl-[0.2rem] max-md:text-[0.5rem] md:pl-[0.397vw] md:text-[0.885vw]"
                aria-hidden="true"
            >
                {PRODUCT_LAUNCH_CAPTION}
            </p>
            <div
                className="flex items-center max-md:mt-[0.35rem] max-md:gap-2 md:mt-[0.77vw] md:gap-[1.094vw]"
                aria-hidden="true"
            >
                {groupsFor(countdown).map((group, index) => (
                    <Fragment key={group.unit}>
                        {index > 0 && <Separator />}
                        <div className="flex flex-col items-center text-center max-md:w-[3.1rem] md:w-[6.51vw]">
                            <span
                                className={cn('w-full', DIGIT_CLASS)}
                                style={{ fontFamily: DIGIT_FONT }}
                                suppressHydrationWarning
                            >
                                {pad(group.value)}
                            </span>
                            <span className="w-full font-sans leading-[1.235] font-normal tracking-[0.16em] max-md:text-[0.45rem] md:text-[0.885vw]">
                                {group.unit}
                            </span>
                        </div>
                    </Fragment>
                ))}
            </div>
        </div>
    );
};
